import { normalizeCollectionStateKey } from '../../scripts/utils.js';

/**
 * @typedef {Window & {
 *   __collectionBfCacheScrollHook?: boolean
 *   __collectionPrefilledPages?: number
 * }} PrefillWindow
 */

/**
 * <load-more-pagination> web component
 * Usage: <load-more-pagination current-page="1" last-page="10" next-title="..." next-url="..."></load-more-pagination>
 * Emits 'loadmore' event with { page } when the button is clicked.
 */
export class LoadMorePagination extends HTMLElement {
  static CACHE_PREFIX = 'collection:grid:';

  static #pageshowListenerBound = false;

  static get observedAttributes() {
    return [
      'current-page', 'last-page', 'page-size', 'total-items',
      'next-title', 'next-url', 'section-id'
    ];
  }

  constructor() {
    super();
    this.handleClick = this.handleClick.bind(this);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleClick);
  }

  attributeChangedCallback() {
    this.render();
  }

  get currentPage() {
    return Number(this.getAttribute('current-page')) || 1;
  }
  get lastPage() {
    return Number(this.getAttribute('last-page')) || 1;
  }
  get pageSize() {
    return Number(this.getAttribute('page-size')) || 1;
  }
  get totalItems() {
    return Number(this.getAttribute('total-items')) || 1;
  }
  get nextTitle() {
    if (!this.hasAttribute('next-title')) return null;
    const raw = this.getAttribute('next-title');
    const trimmed = raw?.trim();
    return trimmed ? trimmed : null;
  }
  get nextUrl() {
    return this.getAttribute('next-url') || '#';
  }
  get sectionId() {
    return this.getAttribute('section-id') || '';
  }

  get isCollectionTemplate() {
    if (typeof Theme === 'undefined') return false;
    const theme = /** @type {any} */ (Theme);
    // Theme.template.name is the base template name (e.g. 'collection'), but guard against
    // a suffixed value (e.g. 'collection.premium_collection') so alternate collection
    // templates still enable grid caching / scroll restoration.
    return theme?.template?.name?.split('.')[0] === 'collection';
  }

  #cacheBaseKey(url = new URL(window.location.href)) {
    return normalizeCollectionStateKey(url);
  }

  #cachePageKey(page, url = new URL(window.location.href)) {
    return `${LoadMorePagination.CACHE_PREFIX}${this.#cacheBaseKey(url)}:page:${page}`;
  }

  #cacheCurrentPageKey(url = new URL(window.location.href)) {
    return `${LoadMorePagination.CACHE_PREFIX}${this.#cacheBaseKey(url)}:current-page`;
  }

  #clearGridCaches() {
    for (let i = sessionStorage.length - 1; i >= 0; i--) {
      const key = sessionStorage.key(i);
      if (key?.startsWith(LoadMorePagination.CACHE_PREFIX)) {
        sessionStorage.removeItem(key);
      }
    }
  }

  #buildSectionRequestUrl(page) {
    const url = new URL(window.location.href);
    url.searchParams.set('page', String(page));
    if (this.sectionId) {
      url.searchParams.set('section_id', this.sectionId);
    }
    url.hash = '';
    return url;
  }

  #parseGridItemsFromHtml(html) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const sectionRoot = this.sectionId
      ? doc.getElementById(`shopify-section-${this.sectionId}`) || doc.querySelector(`[data-section-id="${this.sectionId}"]`)
      : doc;
    return Array.from(sectionRoot?.querySelectorAll('[ref="grid"] > *') ?? []);
  }

  #getCachedCurrentPage() {
    const raw = sessionStorage.getItem(this.#cacheCurrentPageKey());
    const parsed = Number(raw);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
  }

  #storeCachedCurrentPage(page) {
    if (!this.isCollectionTemplate || page < 1) return;
    // Keep this synchronous and tiny so current-page can't drift from snapshot presence.
    try {
      sessionStorage.setItem(this.#cacheCurrentPageKey(), String(page));
    } catch {
      // ignore storage errors
    }
  }

  #storePageNodes(page, nodes) {
    if (!this.isCollectionTemplate || page <= 1 || !nodes.length) return;
    const cacheKey = this.#cachePageKey(page);
    const html = nodes.map((node) => node.outerHTML).join('');
    try {
      sessionStorage.setItem(cacheKey, html);
    } catch {
      // Quota likely exceeded: drop oldest page chunks first and retry.
      try {
        for (let i = 2; i < page; i++) {
          sessionStorage.removeItem(this.#cachePageKey(i));
        }
        sessionStorage.setItem(cacheKey, html);
      } catch {
        // If storage is still full, skip cache gracefully.
      }
    }
  }

  async #fetchPageNodes(page) {
    const response = await fetch(this.#buildSectionRequestUrl(page).toString(), {
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    });
    if (!response.ok) return [];

    const html = await response.text();
    return this.#parseGridItemsFromHtml(html);
  }

  #isReturnToCurrentCollection() {
    const returnUrl = sessionStorage.getItem('collection:return:url');
    if (!returnUrl) return false;

    try {
      const parsedReturnUrl = new URL(returnUrl);
      return this.#cacheBaseKey(parsedReturnUrl) === this.#cacheBaseKey();
    } catch {
      return false;
    }
  }

  /** History Back/Forward — avoid clearing return/scroll session (not all browsers expose this the same way). */
  #isHistoryTraversalNavigation() {
    const nav = /** @type {PerformanceNavigationTiming | undefined} */ (
      performance.getEntriesByType?.('navigation')?.[0]
    );
    if (nav?.type === 'back_forward') return true;

    try {
      const legacy = /** @type {Performance & { navigation?: { type: number } }} */ (performance).navigation;
      if (legacy?.type === 2) return true;
    } catch {
      // ignore
    }

    try {
      const activation = /** @type {Window & { navigation?: { activation?: { navigationType?: string } } }} */ (
        window
      ).navigation?.activation;
      if (activation?.navigationType === 'traverse') return true;
    } catch {
      // ignore
    }

    return false;
  }

  #scrollWindowToRestoreY(y) {
    if (!Number.isFinite(y)) return;
    if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
    const html = document.documentElement;
    const prevBehavior = html.style.scrollBehavior;
    html.style.scrollBehavior = 'auto';
    try {
      window.scrollTo({ top: y, left: 0, behavior: 'instant' });
    } catch {
      window.scrollTo(0, y);
    }
    html.style.scrollBehavior = prevBehavior;
  }

  /**
   * "Return from product" session keys must survive browser Back → Forward → Back.
   * Clear them only on a fresh navigation to this collection (e.g. menu link, reload),
   * not on history back/forward and not when arriving from a product URL (breadcrumbs).
   */
  #clearStaleCollectionReturnState() {
    if (!this.isCollectionTemplate) return;

    if (this.#isHistoryTraversalNavigation()) return;

    let fromProduct = false;
    try {
      const ref = document.referrer;
      if (ref) fromProduct = new URL(ref).pathname.includes('/products/');
    } catch {
      // ignore invalid referrer
    }
    if (fromProduct) return;

    sessionStorage.removeItem('collection:return:url');
    sessionStorage.removeItem(`collection:scroll:${this.#cacheBaseKey()}`);
  }

  handleClick(e) {
    const btn = this.querySelector('a');
    if (!btn) return;
    if (this.isNextState()) {
      // Allow default navigation
      return;
    }
    e.preventDefault();
    if (this.loading) return;
    this.loading = true;
    this.loadMore();
  }

  isNextState() {
    return this.lastVisibleProduct() >= this.totalItems;
  }

  lastVisibleProduct() {
    return Math.min((this.currentPage - 1) * this.pageSize + this.pageSize, this.totalItems);
  }

  async loadMore() {
    const nextPage = this.currentPage + 1;
    try {
      const grid = document.querySelector('[ref="grid"]');
      if (!grid) return;

      const newGridItems = await this.#fetchPageNodes(nextPage);
      newGridItems.forEach((item) => grid.appendChild(item));
      this.setAttribute('current-page', String(nextPage));
      this.#storePageNodes(nextPage, newGridItems);
      this.#storeCachedCurrentPage(nextPage);
    } finally {
      this.loading = false;
      this.render();
    }
  }

  render() {
    const isNext = this.isNextState();
    const label = isNext && this.nextTitle ? `NEXT: ${this.nextTitle}` : 'LOAD MORE';
    const href = isNext ? this.nextUrl : '#';
    const start = this.totalItems === 0 ? 0 : ((this.currentPage - 1) * this.pageSize + 1);
    const end = this.lastVisibleProduct();
    const percent = this.totalItems > 0 ? Math.min((end * 100) / this.totalItems, 100) : 0;
    let quantityText = '';
    const theme = /** @type {any} */ (Theme);
    if (theme?.template?.name === 'blog') {
      quantityText = `${end} of ${this.totalItems} articles`
    } else {
      quantityText = (end === this.totalItems)
        ? `${end} of ${this.totalItems} products`
        : `${start} – ${end} of ${this.totalItems} products`;
    }

    const shouldDisable = isNext && !this.nextTitle;
    this.innerHTML = `
      <div class="flex flex-col items-center my-6">
        <div class="text-center mb-4">
          ${quantityText}
        </div>
        <div class="w-full max-w-sm">
          <div class="bg-border h-px w-full relative">
            <div
              class="pagination-progress-fill bg-foreground h-px absolute top-0 left-0 transition-[width]"
              style="width: ${percent}%;"
            ></div>
          </div>
        </div>
      </div>
      <a
        href="${shouldDisable ? '#' : href}"
        class="button button--primary inline-block ${shouldDisable ? 'hidden' : ''}"
      >${label}</a>
    `;
    // Update aria-label for accessibility
    this.setAttribute('aria-label', label);
  }

  set loading(val) {
    if (val) {
      this.setAttribute('data-loading', 'true');
      this.querySelector('a')?.classList.add('is-loading');
    } else {
      this.removeAttribute('data-loading');
      this.querySelector('a')?.classList.remove('is-loading');
    }
  }
  get loading() {
    return this.getAttribute('data-loading') === 'true';
  }

  restoreCollectionScrollPosition() {
    const currentStateKey = this.#cacheBaseKey();
    const returnUrl = sessionStorage.getItem('collection:return:url');
    const exactScrollKey = `collection:scroll:${currentStateKey}`;
    const storedScroll = sessionStorage.getItem(exactScrollKey);
    if (!returnUrl || !storedScroll) return;

    try {
      const parsedReturnUrl = new URL(returnUrl);
      const returnStateKey = this.#cacheBaseKey(parsedReturnUrl);
      if (returnStateKey !== currentStateKey) return;
    } catch {
      return;
    }

    const y = Number(storedScroll);
    if (!Number.isFinite(y)) return;

    requestAnimationFrame(() => this.#scrollWindowToRestoreY(y));
  }

  async #hydratePagesFromUrl() {
    if (!this.#isReturnToCurrentCollection()) return;

    const urlPage = this.#getCachedCurrentPage();
    if (urlPage <= 1) return;

    const grid = document.querySelector('[ref="grid"]');
    if (!grid) return;

    const prefillWindow = /** @type {PrefillWindow} */ (window);
    const startPage = Math.max(2, Number(prefillWindow.__collectionPrefilledPages || 2));
    // Page 1 is already in server HTML. Rebuild pages 2..urlPage inclusively.
    for (let page = startPage; page <= urlPage; page++) {
      try {
        const cachedHtml = sessionStorage.getItem(this.#cachePageKey(page));
        if (cachedHtml) {
          const template = document.createElement('template');
          template.innerHTML = cachedHtml;
          const nodes = Array.from(template.content.children);
          nodes.forEach((node) => grid.appendChild(node));
          continue;
        }

        const nodes = await this.#fetchPageNodes(page);
        nodes.forEach((node) => grid.appendChild(node));
        this.#storePageNodes(page, nodes);
      } catch {
        // fail silently
      }
    }

    this.setAttribute('current-page', String(urlPage));
    this.#storeCachedCurrentPage(urlPage);
    this.render();
  }

  #prepareCacheLifecycle() {
    if (!this.isCollectionTemplate) {
      this.#clearGridCaches();
    }
  }

  async connectedCallback() {
    if ('scrollRestoration' in history) history.scrollRestoration = 'manual';

    if (!LoadMorePagination.#pageshowListenerBound) {
      LoadMorePagination.#pageshowListenerBound = true;
      window.addEventListener('pageshow', (event) => {
        if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
        const pe = /** @type {PageTransitionEvent} */ (event);
        if (!pe.persisted) return;
        const el = document.querySelector('load-more-pagination');
        if (el instanceof LoadMorePagination) {
          queueMicrotask(() => el.restoreCollectionScrollPosition());
        }
      });
    }

    this.#prepareCacheLifecycle();
    this.#clearStaleCollectionReturnState();
    this.render();
    this.addEventListener('click', this.handleClick);
    await this.#hydratePagesFromUrl();
    this.restoreCollectionScrollPosition();
    window.dispatchEvent(new CustomEvent('collection:grid:hydrated'));
  }
}

if (!customElements.get('load-more-pagination')) {
  customElements.define('load-more-pagination', LoadMorePagination);
}
